import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createPartner } from '../../api/partners';

const steps = [
  'Partner Identity',
  'Capabilities & Equipment',
  'Production Capacity',
  'Integration Setup',
  'Documents & Review',
];

const PARTNER_TIERS = {
  campus_micro_factory: {
    label: 'Campus Micro Factory',
    description: 'Academic partnerships - student-operated with faculty oversight',
    minCapacity: 25,
    maxCapacity: 300,
  },
  micro_factory: {
    label: 'Micro Factory',
    description: 'Small-scale local production for rapid turnaround',
    minCapacity: 50,
    maxCapacity: 500,
  },
  regional_hub: {
    label: 'Regional Hub',
    description: 'Mid-scale regional production with advanced capabilities',
    minCapacity: 500,
    maxCapacity: 5000,
  },
  specialized_center: {
    label: 'Specialized Center',
    description: 'Niche expertise for premium/technical garments',
    minCapacity: 100,
    maxCapacity: 2000,
  },
};

const CAPABILITIES = [
  { id: 'digital_cutting', label: 'Digital Cutting', category: 'cutting' },
  { id: 'laser_cutting', label: 'Laser Cutting', category: 'cutting' },
  { id: 'automated_cutting', label: 'Automated Cutting', category: 'cutting' },
  { id: 'basic_assembly', label: 'Basic Assembly', category: 'assembly' },
  { id: 'advanced_assembly', label: 'Advanced Assembly', category: 'assembly' },
  { id: 'screen_printing', label: 'Screen Printing', category: 'finishing' },
  { id: 'dtg_printing', label: 'DTG Printing', category: 'finishing' },
  { id: 'embroidery', label: 'Embroidery', category: 'finishing' },
  { id: 'heat_press', label: 'Heat Press', category: 'finishing' },
  { id: 'quality_control', label: 'Quality Control', category: 'qa' },
  { id: 'automated_inspection', label: 'Automated Inspection', category: 'qa' },
  { id: 'packaging', label: 'Packaging', category: 'fulfillment' },
  { id: 'fulfillment', label: 'Fulfillment', category: 'fulfillment' },
  { id: 'tuka_tech_integration', label: 'Tuka Tech Integration', category: 'integration' },
  { id: 'clo3d_integration', label: 'CLO3D Integration', category: 'integration' },
  { id: 'sustainable_practices', label: 'Sustainable Practices', category: 'sustainability' },
];

const AddPartnerPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Partner Identity
    legalName: '',
    dbaName: '',
    website: '',
    portfolio: '',
    contacts: {
      primary: { name: '', email: '', phone: '' },
      operations: { name: '', email: '', phone: '' },
      finance: { name: '', email: '', phone: '' },
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
    },

    // Tier & Capabilities
    tier: '',
    capabilities: [],
    equipment: [],
    certifications: [],

    // Production Capacity
    productionCapacity: {
      minUnits: '',
      maxUnits: '',
      monthlyCapacity: '',
    },
    leadTimes: {
      standard: '',
      rush: '',
    },
    shippingRadius: '',
    qualityMetrics: {
      defectRate: '',
      onTimeDelivery: '',
    },
    supportedMaterials: [],
    garmentTypes: [],

    // Integration
    apiAccessLevel: 'standard',
    tukaTechEnabled: false,
    tukaTechVersion: '',
    clo3dEnabled: false,
    clo3dVersion: '',

    // Documents
    documents: [],
    references: [],
    internalNotes: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Partner Identity
        if (!formData.legalName) newErrors.legalName = 'Legal name is required';
        if (!formData.contacts.primary.email) newErrors.primaryEmail = 'Primary contact email is required';
        if (!formData.address.city) newErrors.city = 'City is required';
        break;
      case 1: // Capabilities
        if (!formData.tier) newErrors.tier = 'Tier selection is required';
        if (formData.capabilities.length === 0) newErrors.capabilities = 'Select at least one capability';
        break;
      case 2: // Production Capacity
        if (!formData.productionCapacity.monthlyCapacity) {
          newErrors.monthlyCapacity = 'Monthly capacity is required';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleContactChange = (contactType, field, value) => {
    setFormData(prev => ({
      ...prev,
      contacts: {
        ...prev.contacts,
        [contactType]: { ...prev.contacts[contactType], [field]: value }
      }
    }));
  };

  const handleCapabilityToggle = (capabilityId) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(capabilityId)
        ? prev.capabilities.filter(c => c !== capabilityId)
        : [...prev.capabilities, capabilityId]
    }));
    if (errors.capabilities) {
      setErrors(prev => ({ ...prev, capabilities: undefined }));
    }
  };

  const addEquipment = () => {
    setFormData(prev => ({
      ...prev,
      equipment: [...prev.equipment, { type: '', model: '', throughput: '' }]
    }));
  };

  const removeEquipment = (index) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== index)
    }));
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, { brand: '', contact: '', project: '' }]
    }));
  };

  const removeReference = (index) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    setSubmissionError(null);
    setSubmitting(true);

    try {
      await createPartner(formData);
      navigate('/partners');
    } catch (error) {
      console.error('Failed to submit partner application:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to submit partner application. Please try again.';
      setSubmissionError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPartnerIdentity();
      case 1:
        return renderCapabilitiesEquipment();
      case 2:
        return renderProductionCapacity();
      case 3:
        return renderIntegrationSetup();
      case 4:
        return renderDocumentsReview();
      default:
        return null;
    }
  };

  const renderPartnerIdentity = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Company Information
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Legal Name"
          value={formData.legalName}
          onChange={(e) => handleInputChange('legalName', e.target.value)}
          error={!!errors.legalName}
          helperText={errors.legalName || 'Official registered business name'}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="DBA / Brand Name"
          value={formData.dbaName}
          onChange={(e) => handleInputChange('dbaName', e.target.value)}
          helperText="Operating name if different from legal name"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Website"
          value={formData.website}
          onChange={(e) => handleInputChange('website', e.target.value)}
          placeholder="https://example.com"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Portfolio URL"
          value={formData.portfolio}
          onChange={(e) => handleInputChange('portfolio', e.target.value)}
          placeholder="https://portfolio.example.com"
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Contact Information
        </Typography>
      </Grid>

      {/* Primary Contact */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Primary Contact
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Name"
          value={formData.contacts.primary.name}
          onChange={(e) => handleContactChange('primary', 'name', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Email"
          type="email"
          value={formData.contacts.primary.email}
          onChange={(e) => handleContactChange('primary', 'email', e.target.value)}
          error={!!errors.primaryEmail}
          helperText={errors.primaryEmail}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Phone"
          value={formData.contacts.primary.phone}
          onChange={(e) => handleContactChange('primary', 'phone', e.target.value)}
        />
      </Grid>

      {/* Operations Contact */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
          Operations Contact (Optional)
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Name"
          value={formData.contacts.operations.name}
          onChange={(e) => handleContactChange('operations', 'name', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.contacts.operations.email}
          onChange={(e) => handleContactChange('operations', 'email', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Phone"
          value={formData.contacts.operations.phone}
          onChange={(e) => handleContactChange('operations', 'phone', e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Physical Address
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Street Address"
          value={formData.address.street}
          onChange={(e) => handleNestedInputChange('address', 'street', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="City"
          value={formData.address.city}
          onChange={(e) => handleNestedInputChange('address', 'city', e.target.value)}
          error={!!errors.city}
          helperText={errors.city}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="State/Province"
          value={formData.address.state}
          onChange={(e) => handleNestedInputChange('address', 'state', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="ZIP/Postal Code"
          value={formData.address.zipCode}
          onChange={(e) => handleNestedInputChange('address', 'zipCode', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Country"
          value={formData.address.country}
          onChange={(e) => handleNestedInputChange('address', 'country', e.target.value)}
        />
      </Grid>
    </Grid>
  );

  const renderCapabilitiesEquipment = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Partner Tier
        </Typography>
        <FormControl fullWidth error={!!errors.tier}>
          <InputLabel>Select Tier *</InputLabel>
          <Select
            value={formData.tier}
            onChange={(e) => handleInputChange('tier', e.target.value)}
            label="Select Tier *"
          >
            {Object.entries(PARTNER_TIERS).map(([key, tier]) => (
              <MenuItem key={key} value={key}>
                <Box>
                  <Typography variant="body1">{tier.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tier.description} • Capacity: {tier.minCapacity}-{tier.maxCapacity} units/month
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.tier && <Typography variant="caption" color="error">{errors.tier}</Typography>}
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Manufacturing Capabilities *
        </Typography>
        {errors.capabilities && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.capabilities}</Alert>
        )}
      </Grid>

      {['cutting', 'assembly', 'finishing', 'qa', 'fulfillment', 'integration', 'sustainability'].map(category => {
        const categoryCaps = CAPABILITIES.filter(c => c.category === category);
        if (categoryCaps.length === 0) return null;

        return (
          <Grid item xs={12} md={6} key={category}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {category}
                </Typography>
                <FormGroup>
                  {categoryCaps.map(capability => (
                    <FormControlLabel
                      key={capability.id}
                      control={
                        <Checkbox
                          checked={formData.capabilities.includes(capability.id)}
                          onChange={() => handleCapabilityToggle(capability.id)}
                        />
                      }
                      label={capability.label}
                    />
                  ))}
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>
        );
      })}

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Equipment Inventory
          </Typography>
          <Button startIcon={<AddIcon />} onClick={addEquipment} size="small">
            Add Equipment
          </Button>
        </Box>
      </Grid>

      {formData.equipment.map((equip, index) => (
        <Grid item xs={12} key={index}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Equipment Type"
                  value={equip.type}
                  onChange={(e) => {
                    const newEquipment = [...formData.equipment];
                    newEquipment[index].type = e.target.value;
                    handleInputChange('equipment', newEquipment);
                  }}
                  placeholder="e.g., Laser Cutter"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Model"
                  value={equip.model}
                  onChange={(e) => {
                    const newEquipment = [...formData.equipment];
                    newEquipment[index].model = e.target.value;
                    handleInputChange('equipment', newEquipment);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Throughput"
                  value={equip.throughput}
                  onChange={(e) => {
                    const newEquipment = [...formData.equipment];
                    newEquipment[index].throughput = e.target.value;
                    handleInputChange('equipment', newEquipment);
                  }}
                  placeholder="units/hour"
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <IconButton onClick={() => removeEquipment(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  const renderProductionCapacity = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Production Capacity
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          type="number"
          label="Minimum Units Per Run"
          value={formData.productionCapacity.minUnits}
          onChange={(e) => handleNestedInputChange('productionCapacity', 'minUnits', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">units</InputAdornment>,
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Maximum Units Per Run"
          value={formData.productionCapacity.maxUnits}
          onChange={(e) => handleNestedInputChange('productionCapacity', 'maxUnits', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">units</InputAdornment>,
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          type="number"
          label="Monthly Capacity"
          value={formData.productionCapacity.monthlyCapacity}
          onChange={(e) => handleNestedInputChange('productionCapacity', 'monthlyCapacity', e.target.value)}
          error={!!errors.monthlyCapacity}
          helperText={errors.monthlyCapacity}
          InputProps={{
            endAdornment: <InputAdornment position="end">units/month</InputAdornment>,
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Lead Times & Logistics
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Standard Lead Time"
          value={formData.leadTimes.standard}
          onChange={(e) => handleNestedInputChange('leadTimes', 'standard', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">days</InputAdornment>,
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Rush Lead Time"
          value={formData.leadTimes.rush}
          onChange={(e) => handleNestedInputChange('leadTimes', 'rush', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">days</InputAdornment>,
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Shipping Radius"
          value={formData.shippingRadius}
          onChange={(e) => handleInputChange('shippingRadius', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">miles</InputAdornment>,
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Quality Metrics
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Target Defect Rate"
          value={formData.qualityMetrics.defectRate}
          onChange={(e) => handleNestedInputChange('qualityMetrics', 'defectRate', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          helperText="Percentage of defective units"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="On-Time Delivery Rate"
          value={formData.qualityMetrics.onTimeDelivery}
          onChange={(e) => handleNestedInputChange('qualityMetrics', 'onTimeDelivery', e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          helperText="Percentage of orders delivered on time"
        />
      </Grid>
    </Grid>
  );

  const renderIntegrationSetup = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          API Access Level
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Access Level</InputLabel>
          <Select
            value={formData.apiAccessLevel}
            onChange={(e) => handleInputChange('apiAccessLevel', e.target.value)}
            label="Access Level"
          >
            <MenuItem value="standard">Standard - Basic order and status endpoints</MenuItem>
            <MenuItem value="elevated">Elevated - Full production data access</MenuItem>
            <MenuItem value="restricted">Restricted - Read-only access</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          System Integrations
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tukaTechEnabled}
                  onChange={(e) => handleInputChange('tukaTechEnabled', e.target.checked)}
                />
              }
              label="Tuka Tech Integration"
            />
            {formData.tukaTechEnabled && (
              <TextField
                fullWidth
                label="Tuka Tech Version"
                value={formData.tukaTechVersion}
                onChange={(e) => handleInputChange('tukaTechVersion', e.target.value)}
                placeholder="e.g., 2023.1"
                sx={{ mt: 2 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.clo3dEnabled}
                  onChange={(e) => handleInputChange('clo3dEnabled', e.target.checked)}
                />
              }
              label="CLO3D Integration"
            />
            {formData.clo3dEnabled && (
              <TextField
                fullWidth
                label="CLO3D Version"
                value={formData.clo3dVersion}
                onChange={(e) => handleInputChange('clo3dVersion', e.target.value)}
                placeholder="e.g., 7.2"
                sx={{ mt: 2 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          API key will be generated automatically upon partner approval
        </Alert>
      </Grid>
    </Grid>
  );

  const renderDocumentsReview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          References
        </Typography>
        <Button startIcon={<AddIcon />} onClick={addReference} size="small" sx={{ mb: 2 }}>
          Add Reference
        </Button>
      </Grid>

      {formData.references.map((ref, index) => (
        <Grid item xs={12} key={index}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Brand/Company"
                  value={ref.brand}
                  onChange={(e) => {
                    const newReferences = [...formData.references];
                    newReferences[index].brand = e.target.value;
                    handleInputChange('references', newReferences);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={ref.contact}
                  onChange={(e) => {
                    const newReferences = [...formData.references];
                    newReferences[index].contact = e.target.value;
                    handleInputChange('references', newReferences);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Project Description"
                  value={ref.project}
                  onChange={(e) => {
                    const newReferences = [...formData.references];
                    newReferences[index].project = e.target.value;
                    handleInputChange('references', newReferences);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <IconButton onClick={() => removeReference(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Internal Notes
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Reviewer Notes"
          value={formData.internalNotes}
          onChange={(e) => handleInputChange('internalNotes', e.target.value)}
          placeholder="Internal notes, red flags, special considerations..."
        />
      </Grid>

      <Grid item xs={12}>
        <Alert severity="success" icon={<CheckCircleIcon />}>
          Ready to submit! Review the information above and click "Submit Application" to create this partner.
        </Alert>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/partners')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Add Manufacturing Partner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete all required information to onboard a new partner
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Form Content */}
      <Paper sx={{ p: 3 }}>
        {submissionError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submissionError}
          </Alert>
        )}
        {renderStepContent(activeStep)}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0 || submitting} onClick={handleBack}>
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={() => navigate('/partners')} disabled={submitting}>
              Cancel
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext} disabled={submitting}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddPartnerPage;
